import { Link } from 'react-router-dom'

import { Box, Tag, Text } from '@factorialco/deprecated-design-system'
import { Currency } from 'dinero.js'
import type { ItManagementSoftwareInfoSoftwareSetupFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { getSoftware } from 'modules/it_management/lib/software'

import SoftwareLogo from '../../_SoftwareLogo'
import * as styles from '../index.vanilla.css'

interface Props {
  softwareSetup: ItManagementSoftwareInfoSoftwareSetupFragment
  currency: Currency
}

const SoftwareInfo = ({ softwareSetup, currency }: Props) => {
  const { navigation } = useNavigation()

  const {
    access: { locale },
  } = useCurrentSession()

  const { id } = softwareSetup
  const software = getSoftware(softwareSetup)

  if (!software) return null

  const month = moment().startOf('month').format('M')

  const cost = softwareSetup.monthlySoftwareCosts.find(
    (cost) => moment(cost.monthlyIndexedDate).month() + 1 === parseInt(month)
  )

  const employeesCount = softwareSetup.employeeSoftwareMemberships.length

  const formattedCost = cost?.costInCents
    ? MoneyWithCurrency({
        amount: cost.costInCents,
        currency: currency,
        locale,
      })
    : '--'

  const currentDate = moment().startOf('day')
  const renewalDate = moment(softwareSetup.renewalDate).startOf('day')
  const monthsLeft = renewalDate.diff(currentDate, 'month')
  const formattedRenewalDate = softwareSetup.renewalDate
    ? dateBackendToUI(softwareSetup.renewalDate)
    : ''
  const formattedDate = monthsLeft <= 0 ? formattedRenewalDate : ''

  return (
    <Box width="s400">
      <Link
        to={navigation.software.applications.applicationId.overview.fullPath({
          application_id: id,
        })}
      >
        <Box gap="s16" className={styles.softwareCardInner}>
          <Box flexDirection="row" justifyContent="spaceBetween">
            <Box gap="s8">
              <SoftwareLogo size="s48" software={software} />
              <Text size="300" weight="semibold" color="grey1000">
                {software.name}
              </Text>
            </Box>
            <Box>
              <Tag type="grey">
                <Text size="200" weight="medium" color="grey1000">
                  {i18n.t('it_management.software_setups.list.card.users', {
                    count: employeesCount,
                  })}
                </Text>
              </Tag>
            </Box>
          </Box>
          <Box flexDirection="row" justifyContent="spaceBetween">
            <Box>
              <Text size="200" weight="medium" color="grey1000">
                {formattedCost}
              </Text>
              <Text size="200">
                {i18n.t('it_management.software_setups.list.card.monthly_cost')}
              </Text>
            </Box>
            {formattedDate && (
              <Box alignItems="flexEnd">
                <Text size="200" weight="medium" color="grey1000">
                  {formattedDate}
                </Text>
                <Text size="200">
                  {i18n.t('it_management.software_setups.list.card.renewal_date')}
                </Text>
              </Box>
            )}
          </Box>
        </Box>
      </Link>
    </Box>
  )
}

export default SoftwareInfo
